import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  MessageSquare, X, ArrowLeft, Hash, Users, Wifi, WifiOff, Paperclip, ShieldAlert,
} from 'lucide-react';

// Rótulos CURTOS: aqui cabe uma linha no cabeçalho do widget, não um parágrafo. A frase
// inteira fica na página cheia do chat (`ChatPage.js`). A chave vem do `reason` do
// fechamento — ver `MOTIVOS` em `backend/routes/chat_ws.py`.
const ROTULO_RECUSA = {
  sem_permissao: 'Sem permissão',
  nao_membro: 'Fora desta conversa',
  canal_ausente: 'Conversa removida',
  sessao_revogada: 'Sessão encerrada',
  usuario_inativo: 'Conta inativa',
};
const ROTULO_POR_TIPO = { sessao: 'Sessão expirada', permissao: 'Sem acesso', canal: 'Indisponível' };
import toast from 'react-hot-toast';

import api from '../../utils/api';
import { canAccess } from '../../hooks/usePermission';
import Avatar from './Avatar';
import {
  NAVY, GOLD, fmtDataLabel, mergeMensagem,
} from '../../utils/chatUtils';
import { useChatSocket } from '../../hooks/useChatSocket';
import { useChatAnexos } from './useChatAnexos';
import { criarOnEvento } from './chatWidgetEventos';
import { ListaConversas, ConversaView } from './ChatWidgetViews';

// Repolling do contador (mesmo intervalo do /mobile/contador).
const POLL_MS = 30000;

/**
 * Chat flutuante (FAB no canto inferior direito).
 * - Badge de não-lidas (soma de c.nao_lidas de GET /chat/canais; repolling 30s).
 * - Lista de conversas ordenada por última mensagem, com preview + não-lidas.
 * - Painel compacto de mensagens: abrir marca como lido e permite responder (WS/REST).
 * - Reaproveita useChatSocket p/ tempo real; garante UMA conexão WS: quando a rota
 *   atual é /chat (a página completa já mantém seu WS), o widget não renderiza.
 */
const ChatWidget = () => {
  const location = useLocation();
  const naRotaChat = location.pathname === '/chat' || location.pathname.startsWith('/chat/');

  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [canais, setCanais] = useState([]);
  const [canalAtivo, setCanalAtivo] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [texto, setTexto] = useState('');
  const [conectado, setConectado] = useState(false);
  // Recusa definitiva do servidor. No cabeçalho do widget, "Reconectando..." eterno era a
  // única pista que o usuário sem permissão recebia — e ela dizia o contrário do que ocorria.
  const [recusa, setRecusa] = useState(null);
  const [loadingCanais, setLoadingCanais] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);

  const msgsEndRef = useRef(null);
  const listaRef = useRef(null);
  const inputRef = useRef(null);
  const ultimoTsRef = useRef(null);
  const pendentesRef = useRef(new Map());
  const canalAtivoRef = useRef(null);
  canalAtivoRef.current = canalAtivo;

  const usuarioAtual = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  }, []);
  const meuId = usuarioAtual?.id;

  const totalNaoLidas = useMemo(
    () => canais.reduce((acc, c) => acc + (c.nao_lidas || 0), 0),
    [canais],
  );

  // ---------- carregamento da lista de canais (badge + lista) ----------
  // canaisMutRef conta as mutações vindas do WS (canal_criado/canal_atualizado) e detecta a
  // corrida com o GET. Este GET é caro (não-lidas + última mensagem POR canal), então a janela
  // em que ele fica em voo é larga — e ele repete a cada 30s. Sem o guard, um `canal_criado`
  // que chegasse durante o voo seria apagado da tela pela resposta, que é um snapshot ANTERIOR
  // ao insert: exatamente o bug "criei um grupo, aparece e some" que o evento veio corrigir.
  // O case `canal_criado` sozinho só cobre o evento chegar DEPOIS do refetch.
  const canaisMutRef = useRef(0);
  // LÁPIDES — id -> instante da remoção. Mesmo mecanismo do ChatPage e pela mesma razão: o
  // GET pode ter partido ANTES do arquivamento e trazer de volta o canal que acabou de sair.
  // Guardar só o id tornaria a lápide eterna e uma restauração ficaria invisível para sempre
  // nesta aba — aqui isso é pior ainda, porque o widget nunca desmonta (o early-return de
  // `naRotaChat` vem depois dos hooks), então o Set sobreviveria a toda a navegação.
  const removidosRef = useRef(new Map());
  const carregarCanais = useCallback(async (comLoading = false) => {
    if (comLoading) setLoadingCanais(true);
    const mut = canaisMutRef.current;
    const partiuEm = Date.now();
    try {
      const res = await api.get('/chat/canais');
      const lista = (res.data || []).filter((c) => {
        const removidoEm = removidosRef.current.get(c.id);
        if (removidoEm === undefined) return true;
        if (removidoEm < partiuEm) { removidosRef.current.delete(c.id); return true; }
        return false;
      });
      // Zera localmente o canal aberto (já lido) para o badge não "piscar".
      const ativoId = canalAtivoRef.current?.id;
      const doServidor = ativoId ? lista.map(c => c.id === ativoId ? { ...c, nao_lidas: 0 } : c) : lista;
      setCanais((prev) => {
        if (canaisMutRef.current === mut) return doServidor;   // sem corrida: servidor é a verdade
        // O WS mexeu na lista enquanto o GET voava: substituir o array descartaria o canal
        // recém-chegado (o snapshot é anterior a ele). Une por id — o servidor vence nos que
        // conhece e os que só o WS conhece sobrevivem. Não duplica (a chave é o id) e não
        // ressuscita canal arquivado — `doServidor` já veio filtrado pelas lápides. O
        // `canaisOrdenados` reordena depois, então a posição da concatenação não importa.
        const idsServidor = new Set(doServidor.map(c => c.id));
        const novosDoWs = prev.filter(c => !idsServidor.has(c.id));
        return [...novosDoWs, ...doServidor];
      });
    } catch { /* silencioso: próxima varredura tenta de novo */ }
    finally { if (comLoading) setLoadingCanais(false); }
  }, []);

  // Repolling do badge (só quando o widget está ativo — fora da rota /chat).
  useEffect(() => {
    if (naRotaChat) return undefined;
    carregarCanais();
    const t = setInterval(() => carregarCanais(), POLL_MS);
    return () => clearInterval(t);
  }, [naRotaChat, carregarCanais]);

  // Diretório leve (id/nome/foto) só para exibir as FOTOS nos avatares. Carregado na 1ª
  // ABERTURA do painel e nunca mais: fechado o widget é só FAB + badge (nenhum avatar),
  // e o ref evita rebaixar o diretório a cada ida e volta de /chat.
  const [usuariosPorId, setUsuariosPorId] = useState({});
  const pessoasCarregadasRef = useRef(false);
  useEffect(() => {
    if (naRotaChat || !aberto || pessoasCarregadasRef.current) return;
    pessoasCarregadasRef.current = true;
    api.get('/chat/pessoas')
      .then(r => setUsuariosPorId(Object.fromEntries((r.data || []).map(u => [u.id, u]))))
      // libera o ref: falha de rede não pode prender os avatares nas iniciais para sempre
      .catch(() => { pessoasCarregadasRef.current = false; });
  }, [naRotaChat, aberto]);

  // ---------- scroll ----------
  const scrollToBottom = useCallback((force = false) => {
    const el = listaRef.current;
    if (!force && el) {
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (dist > 140) return;
    }
    requestAnimationFrame(() => msgsEndRef.current?.scrollIntoView({ behavior: 'smooth' }));
  }, []);
  useEffect(() => { scrollToBottom(); }, [mensagens, scrollToBottom]);

  // cursor de resync (maior created_at confirmado)
  useEffect(() => {
    let maior = null;
    for (const m of mensagens) {
      if (String(m.id).startsWith('tmp_')) continue;
      if (m.created_at && (!maior || m.created_at > maior)) maior = m.created_at;
    }
    if (maior) ultimoTsRef.current = maior;
  }, [mensagens]);

  // ---------- WebSocket ----------
  const marcarLido = useCallback((canalId) => {
    if (!canalId) return;
    api.post(`/chat/canais/${canalId}/ler`).catch(() => {});
    setCanais(prev => prev.map(c => c.id === canalId ? { ...c, nao_lidas: 0 } : c));
  }, []);

  const onResync = useCallback(async (canalId) => {
    const after = ultimoTsRef.current;
    if (!canalId || !after) return;
    try {
      const res = await api.get(`/chat/canais/${canalId}/mensagens`, { params: { after, limit: 100 } });
      if (Array.isArray(res.data) && res.data.length) {
        setMensagens(prev => res.data.reduce((acc, d) => mergeMensagem(acc, d), prev));
      }
    } catch { /* a próxima reconexão tenta de novo */ }
  }, []);

  // O SWITCH dos nove eventos mora em `chatWidgetEventos.js` desde o split da regra de 500.
  // A lista de dependencias e a MESMA que o `useCallback` original tinha: a identidade da
  // funcao (que `useChatSocket` recebe) muda exatamente quando mudava antes.
  const onEvento = useMemo(
    () => criarOnEvento({
      canalAtivoRef, setMensagens, setCanais, setCanalAtivo,
      pendentesRef, canaisMutRef, removidosRef, meuId, marcarLido,
    }),
    [meuId, marcarLido],
  );

  const { conectar, fechar, isOpen, sendRaw } = useChatSocket({
    onEvento, onStatus: setConectado, onResync, pendentesRef,
    onRecusado: (r) => setRecusa(r),
  });

  // Uma única conexão: conecta só com painel aberto + canal ativo + fora de /chat.
  useEffect(() => {
    setRecusa(null);   // a recusa era da conversa anterior; a nova pode abrir
    if (!naRotaChat && aberto && canalAtivo?.id) {
      conectar(canalAtivo.id);
      return () => fechar();
    }
    fechar();
    setConectado(false);
    return undefined;
  }, [naRotaChat, aberto, canalAtivo?.id, conectar, fechar]);

  // ---------- abrir canal ----------
  const abrirCanal = useCallback(async (canal) => {
    if (!canal) return;
    setCanalAtivo(canal);
    setMensagens([]);
    setLoadingMsgs(true);
    try {
      const res = await api.get(`/chat/canais/${canal.id}/mensagens`, { params: { limit: 50 } });
      setMensagens(res.data || []);
      marcarLido(canal.id);
    } catch { toast.error('Erro ao carregar mensagens'); }
    finally { setLoadingMsgs(false); setTimeout(() => scrollToBottom(true), 0); }
  }, [marcarLido, scrollToBottom]);

  const voltarLista = useCallback(() => { setCanalAtivo(null); setMensagens([]); }, []);

  // ---------- enviar ----------
  const enviarTexto = useCallback(() => {
    const t = texto.trim();
    const canal = canalAtivoRef.current;
    if (!t || !canal) return;
    const clientMsgId = (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : `${meuId || 'u'}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const payload = { texto: t, client_msg_id: clientMsgId };
    const otimista = {
      id: `tmp_${clientMsgId}`, client_msg_id: clientMsgId, canal_id: canal.id,
      autor_id: meuId, autor_nome: usuarioAtual?.name, texto: t, tipo: 'texto',
      reacoes: [], created_at: new Date().toISOString(), _status: 'enviando',
    };
    setMensagens(prev => [...prev, otimista]);
    pendentesRef.current.set(clientMsgId, { canalId: canal.id, payload });
    if (isOpen()) {
      sendRaw({ tipo: 'mensagem', ...payload });
    } else {
      api.post(`/chat/canais/${canal.id}/mensagens`, payload)
        .then((res) => { setMensagens(prev => mergeMensagem(prev, res.data)); pendentesRef.current.delete(clientMsgId); })
        .catch(() => {
          toast.error('Sem conexão — será reenviada ao reconectar');
          setMensagens(prev => prev.map(m => m.client_msg_id === clientMsgId ? { ...m, _status: 'falhou' } : m));
        });
    }
    setTexto('');
    // Reflete preview otimista na lista.
    setCanais(prev => prev.map(c => c.id === canal.id
      ? { ...c, ultima_mensagem: { texto: t, created_at: otimista.created_at, autor_id: meuId } }
      : c));
  }, [texto, meuId, usuarioAtual, isOpen, sendRaw]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviarTexto(); }
  };

  // ---------- upload + gravação de áudio (extraído para hook co-locado) ----------
  const {
    gravando, arrastando, setArrastando, fileInputRef,
    enviarArquivo, iniciarGravacao, pararGravacao, onDropArquivo, onPasteArquivo,
  } = useChatAnexos({ aberto, naRotaChat, canalAtivo, canalAtivoRef, meuId, setMensagens });

  // ---------- grupos por data ----------
  const grupos = useMemo(() => {
    const out = []; let ultima = null;
    for (const m of mensagens) {
      const label = fmtDataLabel(m.created_at);
      if (label !== ultima) { out.push({ _sep: true, label, key: 'sep-' + m.id }); ultima = label; }
      out.push(m);
    }
    return out;
  }, [mensagens]);

  const canaisOrdenados = useMemo(() => {
    return [...canais].sort((a, b) => {
      const ta = (a.ultima_mensagem || {}).created_at || a.created_at || '';
      const tb = (b.ultima_mensagem || {}).created_at || b.created_at || '';
      return tb.localeCompare(ta);
    });
  }, [canais]);

  // Casa pelo nome da conversa OU pelo nome de um MEMBRO — o pedido é "localizar uma pessoa,
  // seja canal, grupo ou direto": buscar "João" tem que trazer o GRUPO em que o João está, e
  // não só a conversa que por acaso se chama João. Mesma semântica de ChatPage.canaisFiltrados.
  //
  // É VISTA pura sobre `canais`: não remove nada do estado, então limpar a busca traz tudo de
  // volta e um canal que chegue pelo WS durante uma busca não se perde (só não aparece
  // enquanto não casar). A chave da lista continua sendo c.id — não duplica.
  //
  // O canal ABERTO fica de fora do casamento por MEMBRO, mas não por um `if`: `membros_ids`
  // vem VAZIO do servidor nesse tipo (é "todo mundo", não uma lista enumerada). O efeito é o
  // desejado — se casasse, casaria TODA busca por pessoa e viraria ruído. Pelo NOME ele casa.
  //
  // usuariosPorId só existe depois da 1ª abertura do painel (fetch em /chat/pessoas). Se ele
  // ainda estiver em voo, a busca degrada para nome-da-conversa e volta ao normal quando
  // resolver — o fetch dispara na MESMA ação que revela este campo (abrir o painel).
  const canaisFiltrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return canaisOrdenados;
    return canaisOrdenados.filter(c => (c.nome || '').toLowerCase().includes(q)
      || (c.membros_ids || []).some(id => (usuariosPorId[id]?.name || '').toLowerCase().includes(q)));
  }, [canaisOrdenados, busca, usuariosPorId]);

  // Na página /chat o widget não existe (evita 2º WS + UI redundante).
  // A BOLHA SEGUE O MÓDULO. Ela aparece em TODA tela do app, então esconder só o item de menu
  // deixaria o chat perfeitamente utilizável pelo canto da tela — o menu dizendo que não existe
  // e o botão dizendo que sim. `canAccess` já devolve false para módulo desligado
  // (`usePermission.MODULOS_DESLIGADOS`), então esta linha vale tanto para o desligamento quanto
  // para quem simplesmente não tem permissão de chat — que também via a bolha até aqui.
  if (naRotaChat || !canAccess('chat')) return null;

  // Foto do OUTRO participante (o Avatar recebe o id do CANAL, não o da pessoa).
  const fotoDoDireto = (c) => {
    if (c?.tipo !== 'direto') return undefined;
    const outro = (c.membros_ids || []).find(id => id !== meuId);
    return outro ? usuariosPorId[outro]?.foto_url : undefined;
  };

  // Mesma precedência da página /chat: foto do canal > foto do outro (direto) > ícone.
  // O teste de `foto_url` vem PRIMEIRO: num direto o servidor já resolve esse campo por
  // espectador (a foto privada de quem olha, se houver — ver _apelidos_do_usuario), então
  // testar `tipo === 'direto'` antes nunca o alcançava e a foto privada era ignorada.
  const iconeCanal = (c, size) => {
    if (c.foto_url) return <Avatar nome={c.nome} id={c.id} size={size} fotoUrl={c.foto_url} />;
    if (c.tipo === 'direto') return <Avatar nome={c.nome} id={c.id} size={size} fotoUrl={fotoDoDireto(c)} />;
    return (
      <div className="rounded-full flex items-center justify-center flex-shrink-0"
        style={{ width: size, height: size, background: `${NAVY}15`, color: NAVY }}>
        {c.tipo === 'grupo' ? <Users size={size * 0.5} /> : <Hash size={size * 0.5} />}
      </div>
    );
  };

  return (
    <>
      {/* FAB */}
      {!aberto && (
        <button
          onClick={() => setAberto(true)}
          aria-label={totalNaoLidas > 0 ? `Abrir chat, ${totalNaoLidas} não lidas` : 'Abrir chat'}
          className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white hover:scale-105 transition-transform"
          style={{ background: NAVY }}
        >
          <MessageSquare size={24} />
          {totalNaoLidas > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full text-2xs font-bold text-slate-900 flex items-center justify-center border-2 border-white"
              style={{ background: GOLD }}>
              {totalNaoLidas > 99 ? '99+' : totalNaoLidas}
            </span>
          )}
        </button>
      )}

      {/* Painel */}
      {aberto && (
        <div
          className="fixed bottom-5 right-5 z-40 w-[calc(100vw-2.5rem)] sm:w-[360px] h-[520px] max-h-[calc(100vh-2.5rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
          role="dialog" aria-label="Chat"
          onDragOver={canalAtivo ? (e) => {
            // Só intercepta arrasto de ARQUIVO — não quebra o drag de texto para o composer.
            if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
            e.preventDefault();
            if (!arrastando) setArrastando(true);
          } : undefined}
          onDragLeave={canalAtivo ? (e) => {
            // Só desliga ao sair de VERDADE do painel (passar sobre um filho não conta),
            // senão o overlay "Solte para enviar" fica preso na tela.
            if (!e.currentTarget.contains(e.relatedTarget)) setArrastando(false);
          } : undefined}
          onDrop={canalAtivo ? onDropArquivo : undefined}
        >
          {arrastando && canalAtivo && (
            <div className="absolute inset-0 z-30 bg-primary/10 border-2 border-dashed border-primary rounded-2xl flex items-center justify-center pointer-events-none">
              <div className="bg-white px-3 py-1.5 rounded-lg shadow text-primary text-sm font-medium flex items-center gap-2">
                <Paperclip size={15} /> Solte para enviar
              </div>
            </div>
          )}

          {/* header */}
          <div className="h-12 px-3 flex items-center gap-2 flex-shrink-0 text-white" style={{ background: NAVY }}>
            {canalAtivo && (
              <button onClick={voltarLista} aria-label="Voltar para conversas"
                className="p-1 -ml-1 text-white/80 hover:text-white"><ArrowLeft size={18} /></button>
            )}
            {canalAtivo ? (
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {iconeCanal(canalAtivo, 28)}
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{canalAtivo.nome}</div>
                  {/* "Reconectando..." só quando de fato vai reconectar. Com recusa
                      definitiva o texto passa a dizer o motivo — antes ele girava para
                      sempre e era a única pista que o usuário recebia. */}
                  <div
                    className={`text-2xs flex items-center gap-1 ${
                      recusa ? 'text-amber-300' : conectado ? 'text-white/80' : 'text-white/50'}`}
                    title={recusa ? recusa.motivo || '' : ''}
                  >
                    {recusa ? <ShieldAlert size={11} /> : conectado ? <Wifi size={11} /> : <WifiOff size={11} />}
                    {recusa ? ROTULO_RECUSA[recusa.motivo] || ROTULO_POR_TIPO[recusa.tipo] || 'Indisponível'
                      : conectado ? 'Online' : 'Reconectando...'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-1 font-semibold text-sm">
                <MessageSquare size={18} /> Chat
              </div>
            )}
            {/* Limpa a busca ao FECHAR (não ao abrir uma conversa: voltar para a lista deve
                manter o filtro). Reabrir o painel com um filtro velho pareceria "sumiram
                minhas conversas" — e o campo estaria fora de vista, rolado para cima. */}
            <button onClick={() => { setAberto(false); setBusca(''); }} aria-label="Fechar chat"
              className="p-1 text-white/80 hover:text-white"><X size={18} /></button>
          </div>

          {/* corpo */}
          {!canalAtivo ? (
            <ListaConversas
              canais={canais} busca={busca} setBusca={setBusca}
              loadingCanais={loadingCanais} canaisOrdenados={canaisOrdenados}
              canaisFiltrados={canaisFiltrados} abrirCanal={abrirCanal} iconeCanal={iconeCanal}
            />
          ) : (
            <ConversaView
              listaRef={listaRef} msgsEndRef={msgsEndRef} loadingMsgs={loadingMsgs} grupos={grupos}
              meuId={meuId} usuariosPorId={usuariosPorId} gravando={gravando}
              pararGravacao={pararGravacao} fileInputRef={fileInputRef} enviarArquivo={enviarArquivo}
              iniciarGravacao={iniciarGravacao} inputRef={inputRef} texto={texto} setTexto={setTexto}
              onKeyDown={onKeyDown} onPasteArquivo={onPasteArquivo} enviarTexto={enviarTexto}
            />
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
